import styled from '@emotion/styled';
import {
  AdAnalyticsMedia,
  AdAnalyticsMetrics,
  AdAnalyticsPlacement,
  AdAnalyticsPlatform,
  AdAnalyticsVideoSort,
  OrderDirection,
} from '@kaizenplatform/hugot-entity';
import {
  Paper,
  // Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@material-ui/core';
import { List } from 'immutable';
import { createElement, FC } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import CreativePerformanceBrowsingAd from '../../../entity/CreativePerformanceBrowsingAd';
import CreativePerformanceBrowsingCampaign from '../../../entity/CreativePerformanceBrowsingCampaign';
import CreativePerformanceBrowsingCreative from '../../../entity/CreativePerformanceBrowsingCreative';

type Props = {
  analyticsVideos: List<
    CreativePerformanceBrowsingCreative | CreativePerformanceBrowsingCampaign | CreativePerformanceBrowsingAd
  > | null;
  onUpdateSort: (sort: AdAnalyticsVideoSort) => void;
  sort: AdAnalyticsVideoSort | null;
  className?: string;
};

const headRows = [
  {
    id: 'thumbnail',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.thumbnail',
    numeric: false,
    canSort: false,
  },
  {
    id: 'project_id',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.project_id',
    numeric: false,
    canSort: false,
  },
  {
    id: 'platform',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.platform',
    numeric: false,
    canSort: false,
  },
  {
    id: 'media',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.media',
    numeric: false,
    canSort: false,
  },
  {
    id: 'placement',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.placement',
    numeric: false,
    canSort: false,
  },
  { id: 'spend', label: 'adAnalytics_list.anlytics_list_item.detail.column_name.spend', numeric: true, canSort: true },
  {
    id: 'impressions',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.impression',
    numeric: true,
    canSort: true,
  },
  { id: 'clicks', label: 'adAnalytics_list.anlytics_list_item.detail.column_name.click', numeric: true, canSort: true },
  {
    id: 'conversions',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.cv',
    numeric: true,
    canSort: true,
  },
  {
    id: 'view_through_conversions',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.vtcv',
    numeric: true,
    canSort: true,
  },
  {
    id: 'video_p25_watched',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.video_p25_watched',
    numeric: true,
    canSort: true,
  },
  {
    id: 'video_p50_watched',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.video_p50_watched',
    numeric: true,
    canSort: true,
  },
  {
    id: 'video_p75_watched',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.video_p75_watched',
    numeric: true,
    canSort: true,
  },
  {
    id: 'video_p95_watched',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.video_p95_watched',
    numeric: true,
    canSort: true,
  },
  {
    id: 'video_p100_watched',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.video_p100_watched',
    numeric: true,
    canSort: true,
  },
  { id: 'cvr', label: 'adAnalytics_list.anlytics_list_item.detail.column_name.cvr', numeric: true, canSort: true },
  { id: 'cpm', label: 'adAnalytics_list.anlytics_list_item.detail.column_name.cpm', numeric: true, canSort: true },
  { id: 'cpc', label: 'adAnalytics_list.anlytics_list_item.detail.column_name.cpc', numeric: true, canSort: true },
  { id: 'ctr', label: 'adAnalytics_list.anlytics_list_item.detail.column_name.ctr', numeric: true, canSort: true },
  { id: 'cpa', label: 'adAnalytics_list.anlytics_list_item.detail.column_name.cpa', numeric: true, canSort: true },
  {
    id: 'cpa_with_view_through_conversions',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.cpavt',
    numeric: true,
    canSort: true,
  },
  {
    id: 'duration',
    label: 'adAnalytics_list.anlytics_list_item.detail.column_name.duration',
    numeric: true,
    canSort: false,
  },
];

const AnalyticsItemTable: FC<Props> = ({
  analyticsVideos,
  className,
  sort,
  onUpdateSort,
}: {
  analyticsVideos: List<
    CreativePerformanceBrowsingCreative | CreativePerformanceBrowsingCampaign | CreativePerformanceBrowsingAd
  > | null;
  sort: AdAnalyticsVideoSort | null;
  className?: string;
  onUpdateSort: (sort: AdAnalyticsVideoSort) => void;
}) => {
  /**
   * for sort feature
   */
  const handleSort = (key: AdAnalyticsMetrics | null) => {
    if (!key) {
      return;
    }
    let direction = 'desc' as OrderDirection;

    if (sort) {
      if (key === sort.metrics) {
        direction = sort.direction === 'desc' ? 'asc' : 'desc';
      }
    }
    onUpdateSort({
      metrics: key as AdAnalyticsMetrics,
      direction: direction,
    });
  };

  /**
   * for pager feature
   */

  const nf = new Intl.NumberFormat();

  return (
    <Paper>
      <TableWrapper className={className}>
        <Table>
          <TableHead>
            <TableRow>
              {headRows.map((row) => (
                <TableCell key={row.label} sortDirection="asc" padding="default">
                  <TableSortLabel
                    active={sort ? sort.metrics === row.id : false}
                    direction={sort ? sort.direction : undefined}
                    onClick={() => (row.canSort ? handleSort(row.id as AdAnalyticsMetrics) : {})}
                  >
                    <Typography variant="caption" noWrap>
                      <FormattedMessage id={row.label} />
                    </Typography>
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {analyticsVideos ? (
            <TableBody>
              {(analyticsVideos as List<
                | CreativePerformanceBrowsingCreative
                | CreativePerformanceBrowsingCampaign
                | CreativePerformanceBrowsingAd
              >).map((video, index) => (
                <TableRow key={`adAnalytics_list_table_row_${index}`}>
                  <TableCell padding="default">
                    <div>
                      {video.thumbnailImageUrl ? <Img src={video.thumbnailImageUrl} /> : null}
                      {video.adVideoId}
                      {video.title}
                    </div>
                  </TableCell>
                  <TableCell padding="default">
                    {video.project ? (
                      <Link to={`/projects/${video.project.id}/deliverables`} target="_blank">
                        {video.project.title
                          ? video.project.title.length > 30
                            ? video.project.title.slice(0, 30) + '...'
                            : video.project.title
                          : video.project.id}
                      </Link>
                    ) : null}
                  </TableCell>
                  <TableCell padding="default">
                    {video.platforms.map((platform: AdAnalyticsPlatform) => platform.name).join(', ')}
                  </TableCell>
                  <TableCell padding="default">
                    {video.medias.map((medias: AdAnalyticsMedia) => medias.name).join(', ')}
                  </TableCell>
                  <TableCell padding="default">
                    {video.placements.map((placement: AdAnalyticsPlacement) => placement.name).join(', ')}
                  </TableCell>
                  <TableCell padding="default">{video.spend !== null ? video.spend : '-'}</TableCell>
                  <TableCell padding="default">
                    {video.impressions !== null ? nf.format(video.impressions) : '-'}
                  </TableCell>
                  <TableCell padding="default">{video.clicks !== null ? nf.format(video.clicks) : '-'}</TableCell>
                  <TableCell padding="default">
                    <CvNum>{video.conversions !== null ? nf.format(video.conversions) : '-'}</CvNum>
                    <CvTag>
                      {video.conversionsKey ? (
                        <FormattedMessage id={`adAnalytics_cv_key.${video.conversionsKey}`} />
                      ) : (
                        '-'
                      )}
                    </CvTag>
                  </TableCell>
                  <TableCell padding="default">
                    {video.viewThroughConversions !== null ? nf.format(video.viewThroughConversions) : '-'}
                  </TableCell>
                  <TableCell padding="default">
                    {video.videoP25Watched !== null ? nf.format(video.videoP25Watched) : '-'}
                  </TableCell>
                  <TableCell padding="default">
                    {video.videoP50Watched !== null ? nf.format(video.videoP50Watched) : '-'}
                  </TableCell>
                  <TableCell padding="default">
                    {video.videoP75Watched !== null ? nf.format(video.videoP75Watched) : '-'}
                  </TableCell>
                  <TableCell padding="default">
                    {video.videoP95Watched !== null ? nf.format(video.videoP95Watched) : '-'}
                  </TableCell>
                  <TableCell padding="default">
                    {video.videoP100Watched !== null ? nf.format(video.videoP100Watched) : '-'}
                  </TableCell>
                  <TableCell padding="default">{video.cvr !== null ? video.cvr : '-'}</TableCell>
                  <TableCell padding="default">{video.cpm !== null ? video.cpm : '-'}</TableCell>
                  <TableCell padding="default">{video.cpc !== null ? video.cpc : '-'}</TableCell>
                  <TableCell padding="default">{video.ctr !== null ? video.ctr : '-'}</TableCell>
                  <TableCell padding="default">{video.cpa !== null ? video.cpa : '-'}</TableCell>
                  <TableCell padding="default">
                    {video.cpaWithViewThroughConversions !== null ? video.cpaWithViewThroughConversions : '-'}
                  </TableCell>
                  <TableCell padding="default">{video.duration}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          ) : null}
        </Table>
      </TableWrapper>
    </Paper>
  );
};

const TableWrapper = styled('div')({
  overflowX: 'scroll',
});

const Img = styled('img')({
  objectFit: 'contain',
  width: '80px',
  height: '60px',
}) as any;

const CvNum = styled('p')({
  padding: 0,
  marginBottom: '4px',
});

const CvTag = styled('p')({
  padding: 0,
  fontSize: '10px',
  color: 'rgba(0, 0, 0, .66)',
});

export default AnalyticsItemTable;
